import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Space,
  Card,
  Tag,
  message,
  Tooltip,
  Popconfirm,
  Input,
  Row,
  Col,
  Select,
  DatePicker,
  Collapse,
} from "antd";
import dayjs from "dayjs";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  SearchOutlined,
  CloudUploadOutlined,
  SyncOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { fetchInvoices, deleteInvoice } from "../../services/invoiceService";
import { Invoice } from "../../types/invoice";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { getStatusColor as getBookingStatusColor } from "../../utils/bookingUtils";

const { Option } = Select;

interface InvoiceListProps {
  onCreateClick: () => void;
  onPreviewClick?: (invoice: Invoice) => void;
  onEditClick: (invoice: Invoice) => void;
  onRefresh: () => void;
  onCreateMoneybirdInvoice?: (invoice: Invoice) => void;
  onUpdateMoneybirdInvoice?: (invoice: Invoice) => void;
  moneybirdLoading?: {
    create: boolean;
    update: boolean;
    syncAll: boolean;
  };
}

const InvoiceList: React.FC<InvoiceListProps> = ({
  onCreateClick,
  onPreviewClick,
  onEditClick,
  onRefresh,
  onCreateMoneybirdInvoice,
  onUpdateMoneybirdInvoice,
  moneybirdLoading,
}) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [selectedSyncStatus, setSelectedSyncStatus] = useState<string | null>(
    null
  );
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<
    string | null
  >(null);
  const [selectedBookingStatus, setSelectedBookingStatus] = useState<
    string | null
  >(null);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [showGraph, setShowGraph] = useState(false);

  // Get unique companies for filter dropdown
  const uniqueCompanies = React.useMemo(() => {
    interface CompanyInfo {
      id: number;
      name: string;
    }

    const companies = invoices
      .map((invoice) => invoice.booking?.company_info)
      .filter(Boolean)
      .map((company) => (Array.isArray(company) ? company[0] : company))
      .filter(Boolean);

    // Remove duplicates by ID
    const unique = companies.reduce((acc: CompanyInfo[], company) => {
      if (company && !acc.some((c) => c.id === company.id)) {
        acc.push(company as CompanyInfo);
      }
      return acc;
    }, [] as CompanyInfo[]);

    return unique.sort((a, b) => a.name.localeCompare(b.name));
  }, [invoices]);

  // Get unique sync statuses
  const uniqueSyncStatuses = React.useMemo(() => {
    const statuses = invoices
      .map((invoice) => invoice.moneybird_sync_status)
      .filter(Boolean);
    return [...new Set(statuses)].sort();
  }, [invoices]);

  // Get unique payment statuses
  const uniquePaymentStatuses = React.useMemo(() => {
    const statuses = invoices
      .map((invoice) => invoice.moneybird_payment_status)
      .filter(Boolean);
    return [...new Set(statuses)].sort();
  }, [invoices]);

  // Get unique booking statuses
  const uniqueBookingStatuses = React.useMemo(() => {
    const statuses = invoices
      .map((invoice) => invoice.booking?.status)
      .filter(Boolean);
    return [...new Set(statuses)].sort();
  }, [invoices]);

  // Calculate stacked revenue data by due date
  const stackedRevenueData = useMemo(() => {
    const dueDateData: {
      [key: string]: {
        [status: string]: { invoices: Invoice[]; total: number };
      };
    } = {};

    filteredInvoices.forEach((invoice) => {
      if (invoice.lines && invoice.moneybird_due_date) {
        const total = invoice.lines.reduce((sum, line) => {
          return sum + (line.quantity || 0) * (line.unit_price || 0);
        }, 0);

        const dueDate = dayjs(invoice.moneybird_due_date).format("YYYY-MM-DD");
        const status = invoice.moneybird_payment_status || "unknown";

        if (!dueDateData[dueDate]) {
          dueDateData[dueDate] = {};
        }

        if (!dueDateData[dueDate][status]) {
          dueDateData[dueDate][status] = { invoices: [], total: 0 };
        }

        dueDateData[dueDate][status].invoices.push(invoice);
        dueDateData[dueDate][status].total += total;
      }
    });

    // Convert to array and sort by due date
    return Object.entries(dueDateData)
      .map(([date, statusData]) => {
        const paid = statusData.paid?.total || 0;
        const partial = statusData.partial?.total || 0;
        const cancelled = statusData.cancelled?.total || 0;
        const open = statusData.open?.total || 0;
        const overdue = statusData.overdue?.total || 0;
        const unknown = statusData.unknown?.total || 0;

        const total = paid + partial + cancelled + open + overdue + unknown;

        return {
          date,
          total,
          invoices: Object.values(statusData).flatMap((data) => data.invoices),
          segments: [
            {
              status: "paid",
              amount: paid,
              color: "#52c41a",
              label: "Paid",
              invoices: statusData.paid?.invoices || [],
            },
            {
              status: "partial",
              amount: partial,
              color: "#fa8c16",
              label: "Partial",
              invoices: statusData.partial?.invoices || [],
            },
            {
              status: "open",
              amount: open,
              color: "#1890ff",
              label: "Open",
              invoices: statusData.open?.invoices || [],
            },
            {
              status: "overdue",
              amount: overdue,
              color: "#ff4d4f",
              label: "Overdue",
              invoices: statusData.overdue?.invoices || [],
            },
            {
              status: "cancelled",
              amount: cancelled,
              color: "#d9d9d9",
              label: "Cancelled",
              invoices: statusData.cancelled?.invoices || [],
            },
            {
              status: "unknown",
              amount: unknown,
              color: "#722ed1",
              label: "Unknown",
              invoices: statusData.unknown?.invoices || [],
            },
          ].filter((segment) => segment.amount > 0),
        };
      })
      .filter((item) => item.total > 0)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [filteredInvoices]);

  // Load invoices
  const loadInvoices = async () => {
    setLoading(true);
    try {
      const { data, error } = await fetchInvoices();
      if (error) throw error;

      setInvoices(data as Invoice[]);
    } catch (error) {
      console.error("Error loading invoices:", error);
      message.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  // Load invoices on component mount
  useEffect(() => {
    loadInvoices();
  }, []);

  // Filter invoices based on all filters
  useEffect(() => {
    let filtered = [...invoices];

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((invoice) => {
        const bookingRef = invoice.booking?.reference?.toLowerCase() || "";
        const bookingId = `eff${invoice.booking_id}`.toLowerCase();
        const companyName = invoice.booking?.company_info
          ? (Array.isArray(invoice.booking.company_info)
              ? invoice.booking.company_info[0]?.name?.toLowerCase()
              : invoice.booking.company_info.name?.toLowerCase()) || ""
          : "";

        return (
          bookingRef.includes(searchLower) ||
          bookingId.includes(searchLower) ||
          companyName.includes(searchLower)
        );
      });
    }

    // Apply company filter
    if (selectedCompanyId) {
      filtered = filtered.filter((invoice) => {
        const companyId = invoice.booking?.company_info
          ? Array.isArray(invoice.booking.company_info)
            ? invoice.booking.company_info[0]?.id
            : invoice.booking.company_info.id
          : null;
        return companyId === selectedCompanyId;
      });
    }

    // Apply sync status filter
    if (selectedSyncStatus) {
      filtered = filtered.filter(
        (invoice) => invoice.moneybird_sync_status === selectedSyncStatus
      );
    }

    // Apply payment status filter
    if (selectedPaymentStatus) {
      filtered = filtered.filter(
        (invoice) => invoice.moneybird_payment_status === selectedPaymentStatus
      );
    }

    // Apply booking status filter
    if (selectedBookingStatus) {
      filtered = filtered.filter(
        (invoice) => invoice.booking?.status === selectedBookingStatus
      );
    }

    // Apply date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = new Date(dateRange[0]);
      const endDate = new Date(dateRange[1]);
      endDate.setHours(23, 59, 59, 999); // Include the entire end date

      filtered = filtered.filter((invoice) => {
        const invoiceDate = new Date(invoice.created_at || "");
        return invoiceDate >= startDate && invoiceDate <= endDate;
      });
    }

    setFilteredInvoices(filtered);
  }, [
    invoices,
    searchText,
    selectedCompanyId,
    selectedSyncStatus,
    selectedPaymentStatus,
    selectedBookingStatus,
    dateRange,
  ]);

  // Handle invoice deletion
  const handleDelete = async (id: number) => {
    try {
      const { success, error } = await deleteInvoice(id);
      if (error) throw error;

      if (success) {
        message.success("Invoice deleted successfully");
        loadInvoices();
        onRefresh();
      }
    } catch (error) {
      console.error("Error deleting invoice:", error);
      message.error("Failed to delete invoice");
    }
  };

  // Table columns
  const columns = [
    {
      title: "Booking ID",
      key: "booking_id",
      render: (_: unknown, record: Invoice) => {
        if (!record.booking_id) return "-";

        const transportType = record.booking?.transport_type_info
          ? Array.isArray(record.booking.transport_type_info)
            ? record.booking.transport_type_info[0]?.abbreviation || ""
            : record.booking.transport_type_info.abbreviation || ""
          : "";

        return `EFF${record.booking_id}${transportType}`;
      },
    },
    {
      title: "Company",
      key: "company_name",
      render: (_: unknown, record: Invoice) => {
        if (!record.booking?.company_info) return "-";

        return Array.isArray(record.booking.company_info)
          ? record.booking.company_info[0]?.name || "-"
          : record.booking.company_info.name || "-";
      },
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text: string) => formatDate(text),
    },
    {
      title: "Moneybird Details",
      key: "moneybird_details",
      width: 200,
      render: (_: unknown, record: Invoice) => {
        if (!record.moneybird_invoice_id) {
          return <span style={{ color: "#999" }}>Not in Moneybird</span>;
        }

        // Calculate days until due
        const getDaysUntilDue = (dueDate: string) => {
          const due = new Date(dueDate);
          const today = new Date();
          const diffTime = due.getTime() - today.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays < 0) {
            return `${Math.abs(diffDays)} days overdue`;
          } else if (diffDays === 0) {
            return "Due today";
          } else {
            return `${diffDays} days left`;
          }
        };

        return (
          <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
            {record.moneybird_due_date && (
              <div>
                <strong>Due:</strong> {formatDate(record.moneybird_due_date)}
                <div style={{ color: "#fa8c16", fontSize: "11px" }}>
                  {getDaysUntilDue(record.moneybird_due_date)}
                </div>
              </div>
            )}
            {record.moneybird_sent_at && (
              <div style={{ color: "#52c41a" }}>
                <strong>Sent:</strong> {formatDate(record.moneybird_sent_at)}
              </div>
            )}
            {record.moneybird_paid_at && (
              <div style={{ color: "#1890ff" }}>
                <strong>Paid:</strong> {formatDate(record.moneybird_paid_at)}
              </div>
            )}
            {(record.moneybird_reminder_count ?? 0) > 0 && (
              <div style={{ color: "#fa8c16" }}>
                <strong>Reminders:</strong> {record.moneybird_reminder_count}
                {record.moneybird_next_reminder && (
                  <span>
                    {" "}
                    (Next: {formatDate(record.moneybird_next_reminder)})
                  </span>
                )}
              </div>
            )}
            {record.moneybird_invoice_invoice_id && (
              <div style={{ color: "#722ed1" }}>
                <strong>Invoice ID:</strong>{" "}
                {record.moneybird_invoice_invoice_id}
              </div>
            )}
          </div>
        );
      },
    },

    {
      title: "Moneybird Status",
      key: "moneybird_status",
      render: (_: unknown, record: Invoice) => {
        if (!record.moneybird_sync_status) {
          return <Tag color="default">Not Synced</Tag>;
        }

        const getStatusColor = (status: string) => {
          switch (status) {
            case "synced":
            case "updated":
              return "success";
            case "pending":
              return "processing";
            case "failed":
              return "error";
            case "cancelled":
              return "default";
            default:
              return "default";
          }
        };

        const getStatusLabel = (status: string) => {
          switch (status) {
            case "synced":
              return "Synced";
            case "updated":
              return "Updated";
            case "pending":
              return "Pending";
            case "failed":
              return "Failed";
            case "cancelled":
              return "Cancelled";
            default:
              return status;
          }
        };

        const getPaymentStatusColor = (status: string) => {
          switch (status) {
            case "paid":
              return "success";
            case "partial":
              return "processing";
            case "overdue":
              return "error";
            case "cancelled":
              return "default";
            case "open":
            default:
              return "warning";
          }
        };

        const getPaymentStatusIcon = (status: string) => {
          switch (status) {
            case "paid":
              return "💰";
            case "partial":
              return "💸";
            case "overdue":
              return "⚠️";
            case "cancelled":
              return "❌";
            case "open":
            default:
              return "⏳";
          }
        };

        const getPaymentStatusLabel = (status: string) => {
          switch (status) {
            case "paid":
              return "Paid";
            case "partial":
              return "Partially Paid";
            case "overdue":
              return "Overdue";
            case "cancelled":
              return "Cancelled";
            case "open":
              return "Open";
            default:
              return status.charAt(0).toUpperCase() + status.slice(1);
          }
        };

        return (
          <div>
            <Tag color={getStatusColor(record.moneybird_sync_status)}>
              {getStatusLabel(record.moneybird_sync_status)}
            </Tag>
            {record.moneybird_payment_status && (
              <Tooltip
                title={`Moneybird Payment Status: ${getPaymentStatusLabel(
                  record.moneybird_payment_status
                )}${
                  record.moneybird_synced_at
                    ? ` (Last synced: ${formatDate(
                        record.moneybird_synced_at
                      )})`
                    : ""
                }`}
              >
                <Tag
                  color={getPaymentStatusColor(record.moneybird_payment_status)}
                  style={{ marginTop: 4, cursor: "pointer" }}
                >
                  {getPaymentStatusIcon(record.moneybird_payment_status)}{" "}
                  {getPaymentStatusLabel(record.moneybird_payment_status)}
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Booking Status",
      key: "booking_status",
      render: (_: unknown, record: Invoice) => {
        if (!record.booking?.status) return "-";

        return (
          <Tag color={getBookingStatusColor(record.booking.status)}>
            {record.booking.status.charAt(0).toUpperCase() +
              record.booking.status.slice(1).replace(/_/g, " ")}
          </Tag>
        );
      },
    },
    {
      title: "Total",
      key: "total",
      render: (_: unknown, record: Invoice) => {
        const subtotal = (record.lines || []).reduce((sum, line) => {
          const lineAmount = (line.quantity || 0) * (line.unit_price || 0);
          return sum + lineAmount;
        }, 0);

        return formatCurrency(subtotal, record.currency);
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: Invoice) => (
        <Space size="small" wrap>
          {onPreviewClick && (
            <Tooltip title="Preview">
              <Button
                icon={<FileSearchOutlined />}
                onClick={() => onPreviewClick(record)}
                type="text"
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => onEditClick(record)}
              type="text"
              disabled={false} // All invoices can be edited now (only DRAFT or SENT)
            />
          </Tooltip>

          {/* Moneybird Actions */}
          {onCreateMoneybirdInvoice && !record.moneybird_invoice_id && (
            <Tooltip title="Send to Moneybird">
              <Button
                icon={<CloudUploadOutlined />}
                onClick={() => onCreateMoneybirdInvoice(record)}
                type="text"
                loading={moneybirdLoading?.create}
                style={{ color: "#1890ff" }}
              />
            </Tooltip>
          )}

          {onUpdateMoneybirdInvoice && record.moneybird_invoice_id && (
            <Tooltip title="Update in Moneybird">
              <Button
                icon={<SyncOutlined />}
                onClick={() => onUpdateMoneybirdInvoice(record)}
                type="text"
                loading={moneybirdLoading?.update}
                style={{ color: "#52c41a" }}
              />
            </Tooltip>
          )}

          {record.moneybird_invoice_id && (
            <Tooltip title="Open in Moneybird">
              <Button
                icon={<LinkOutlined />}
                onClick={() => {
                  const administrationId = "443982818600552286"; // Correct Moneybird administration ID
                  const url = `https://moneybird.com/${administrationId}/sales_invoices/${record.moneybird_invoice_id}`;
                  console.log("Opening Moneybird URL:", url); // Debug log
                  console.log("Invoice ID:", record.moneybird_invoice_id); // Debug log
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
                type="text"
                style={{ color: "#722ed1" }}
              />
            </Tooltip>
          )}

          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this invoice?"
              onConfirm={() => handleDelete(record.id!)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                type="text"
                disabled={false} // All invoices can be deleted now (only DRAFT or SENT)
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Invoices"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={onCreateClick}>
          Create Invoice
        </Button>
      }
    >
      {/* Search and Filter Controls */}
      <Row gutter={[8, 16]} style={{ marginBottom: 16 }}>
        {/* First Row - Search and Main Filters */}
        <Col span={6}>
          <Input
            placeholder="Search by booking ID or company..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </Col>
        <Col span={4}>
          <Select
            placeholder="Company"
            style={{ width: "100%" }}
            value={selectedCompanyId}
            onChange={(value) => setSelectedCompanyId(value)}
            allowClear
          >
            {uniqueCompanies.map((company) => (
              <Option key={company.id} value={company.id}>
                {company.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={6}>
          <DatePicker.RangePicker
            style={{ width: "100%" }}
            placeholder={["Start Date", "End Date"]}
            value={
              dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null
            }
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([
                  dates[0].format("YYYY-MM-DD"),
                  dates[1].format("YYYY-MM-DD"),
                ]);
              } else {
                setDateRange(null);
              }
            }}
            allowClear
          />
        </Col>
        <Col span={3}>
          <span style={{ color: "#666", fontSize: "14px" }}>
            {filteredInvoices.length} of {invoices.length} invoices
          </span>
        </Col>
      </Row>

      <Row gutter={[8, 16]} style={{ marginBottom: 16 }}>
        {/* Second Row - Status Filters */}
        <Col span={4}>
          <Select
            placeholder="Sync Status"
            style={{ width: "100%" }}
            value={selectedSyncStatus}
            onChange={(value) => setSelectedSyncStatus(value)}
            allowClear
          >
            {uniqueSyncStatuses.map((status) => (
              <Option key={status} value={status}>
                {status
                  ? status.charAt(0).toUpperCase() + status.slice(1)
                  : status}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={4}>
          <Select
            placeholder="Payment Status"
            style={{ width: "100%" }}
            value={selectedPaymentStatus}
            onChange={(value) => setSelectedPaymentStatus(value)}
            allowClear
          >
            {uniquePaymentStatuses.map((status) => (
              <Option key={status} value={status}>
                {status
                  ? status.charAt(0).toUpperCase() + status.slice(1)
                  : status}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={4}>
          <Select
            placeholder="Booking Status"
            style={{ width: "100%" }}
            value={selectedBookingStatus}
            onChange={(value) => setSelectedBookingStatus(value)}
            allowClear
          >
            {uniqueBookingStatuses.map((status) => (
              <Option key={status} value={status}>
                {status
                  ? status.charAt(0).toUpperCase() +
                    status.slice(1).replace(/_/g, " ")
                  : status}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={8}>
          {/* Clear All Filters Button */}
          <Button
            onClick={() => {
              setSearchText("");
              setSelectedCompanyId(null);
              setSelectedSyncStatus(null);
              setSelectedPaymentStatus(null);
              setSelectedBookingStatus(null);
              setDateRange(null);
            }}
          >
            Clear All Filters
          </Button>

          {/* Toggle Graph Button */}
          <Button
            onClick={() => setShowGraph(!showGraph)}
            style={{ marginLeft: 8 }}
            type={showGraph ? "primary" : "default"}
          >
            {showGraph ? "Hide Graph" : "Show Graph"}
          </Button>
        </Col>
      </Row>

      {/* Graph Section */}
      {showGraph && (
        <Collapse
          style={{ marginBottom: 16 }}
          items={[
            {
              key: "graph",
              label: "Revenue by Due Date with Payment Status",
              children: (
                <div
                  style={{ minHeight: 400, padding: 16, overflow: "visible" }}
                >
                  {stackedRevenueData.length > 0 ? (
                    <div>
                      <div
                        style={{
                          marginBottom: 16,
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        Revenue grouped by invoice due dates with payment status
                        breakdown (stacked bars)
                      </div>

                      {/* Legend */}
                      <div
                        style={{
                          marginBottom: 20,
                          padding: "12px 16px",
                          backgroundColor: "#fafafa",
                          borderRadius: "6px",
                          border: "1px solid #f0f0f0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            marginBottom: 8,
                            color: "#333",
                          }}
                        >
                          Payment Status Legend:
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "16px",
                            fontSize: "11px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <div
                              style={{
                                width: 12,
                                height: 12,
                                backgroundColor: "#52c41a",
                                borderRadius: 2,
                              }}
                            />
                            <span>Paid</span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <div
                              style={{
                                width: 12,
                                height: 12,
                                backgroundColor: "#fa8c16",
                                borderRadius: 2,
                              }}
                            />
                            <span>Partial</span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <div
                              style={{
                                width: 12,
                                height: 12,
                                backgroundColor: "#1890ff",
                                borderRadius: 2,
                              }}
                            />
                            <span>Open</span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <div
                              style={{
                                width: 12,
                                height: 12,
                                backgroundColor: "#ff4d4f",
                                borderRadius: 2,
                              }}
                            />
                            <span>Overdue</span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <div
                              style={{
                                width: 12,
                                height: 12,
                                backgroundColor: "#d9d9d9",
                                borderRadius: 2,
                              }}
                            />
                            <span>Cancelled</span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <div
                              style={{
                                width: 12,
                                height: 12,
                                backgroundColor: "#722ed1",
                                borderRadius: 2,
                              }}
                            />
                            <span>Unknown</span>
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          alignItems: "end",
                        }}
                      >
                        {stackedRevenueData.map((item) => {
                          const maxTotal = Math.max(
                            ...stackedRevenueData.map((d) => d.total)
                          );
                          const heightPercentage =
                            (item.total / maxTotal) * 100;

                          return (
                            <div
                              key={item.date}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                minWidth: 80,
                                textAlign: "center",
                              }}
                            >
                              {/* Stacked Bar */}
                              <Tooltip
                                title={
                                  <div style={{ maxWidth: 300 }}>
                                    <div
                                      style={{
                                        fontWeight: "bold",
                                        marginBottom: 8,
                                        fontSize: "13px",
                                      }}
                                    >
                                      Due:{" "}
                                      {dayjs(item.date).format("MMM DD, YYYY")}{" "}
                                      - €{item.total.toFixed(2)}
                                    </div>
                                    <div
                                      style={{
                                        maxHeight: 200,
                                        overflowY: "auto",
                                      }}
                                    >
                                      {item.invoices.map((invoice) => {
                                        const invoiceTotal = (
                                          invoice.lines || []
                                        ).reduce((sum, line) => {
                                          return (
                                            sum +
                                            (line.quantity || 0) *
                                              (line.unit_price || 0)
                                          );
                                        }, 0);

                                        const transportType = invoice.booking
                                          ?.transport_type_info
                                          ? Array.isArray(
                                              invoice.booking
                                                .transport_type_info
                                            )
                                            ? invoice.booking
                                                .transport_type_info[0]
                                                ?.abbreviation || ""
                                            : invoice.booking
                                                .transport_type_info
                                                .abbreviation || ""
                                          : "";

                                        const companyName = invoice.booking
                                          ?.company_info
                                          ? Array.isArray(
                                              invoice.booking.company_info
                                            )
                                            ? invoice.booking.company_info[0]
                                                ?.name || "Unknown"
                                            : invoice.booking.company_info
                                                .name || "Unknown"
                                          : "Unknown";

                                        return (
                                          <div
                                            key={invoice.id}
                                            style={{
                                              marginBottom: 6,
                                              paddingBottom: 6,
                                              borderBottom: "1px solid #f0f0f0",
                                              fontSize: "11px",
                                            }}
                                          >
                                            <div style={{ fontWeight: "bold" }}>
                                              EFF{invoice.booking_id}
                                              {transportType} - {companyName}
                                            </div>
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                color: "#666",
                                              }}
                                            >
                                              <span>
                                                Status:{" "}
                                                {(
                                                  invoice.moneybird_payment_status ||
                                                  "unknown"
                                                )
                                                  .charAt(0)
                                                  .toUpperCase() +
                                                  (
                                                    invoice.moneybird_payment_status ||
                                                    "unknown"
                                                  ).slice(1)}
                                              </span>
                                              <span
                                                style={{ fontWeight: "bold" }}
                                              >
                                                €{invoiceTotal.toFixed(2)}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                }
                                placement="top"
                                overlayStyle={{ maxWidth: 350 }}
                              >
                                <div
                                  style={{
                                    width: 40,
                                    height: Math.max(heightPercentage * 2, 10),
                                    borderRadius: "4px 4px 0 0",
                                    marginBottom: 4,
                                    display: "flex",
                                    flexDirection: "column-reverse",
                                    overflow: "hidden",
                                    cursor: "pointer",
                                  }}
                                >
                                  {item.segments.map((segment) => {
                                    const segmentHeight =
                                      (segment.amount / item.total) * 100;
                                    return (
                                      <div
                                        key={segment.status}
                                        style={{
                                          height: `${segmentHeight}%`,
                                          backgroundColor: segment.color,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "white",
                                          fontSize: "8px",
                                          fontWeight: "bold",
                                        }}
                                        title={`${
                                          segment.label
                                        }: €${segment.amount.toFixed(2)}`}
                                      >
                                        {segmentHeight > 15 && (
                                          <>
                                            €
                                            {segment.amount > 1000
                                              ? `${(
                                                  segment.amount / 1000
                                                ).toFixed(1)}k`
                                              : segment.amount.toFixed(0)}
                                          </>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </Tooltip>

                              {/* Total amount label */}
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#333",
                                  fontWeight: "bold",
                                  marginBottom: 2,
                                }}
                              >
                                €
                                {item.total > 1000
                                  ? `${(item.total / 1000).toFixed(1)}k`
                                  : item.total.toFixed(0)}
                              </div>

                              {/* Date label */}
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#666",
                                  transform: "rotate(-45deg)",
                                  transformOrigin: "center",
                                  whiteSpace: "nowrap",
                                  marginTop: 4,
                                }}
                              >
                                {dayjs(item.date).format("MMM DD")}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        color: "#999",
                        fontSize: "14px",
                      }}
                    >
                      No data to display. Invoices need due dates and line items
                      to appear in the graph.
                    </div>
                  )}
                </div>
              ),
            },
          ]}
          defaultActiveKey={showGraph ? ["graph"] : []}
        />
      )}

      <Table
        dataSource={filteredInvoices}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          total: filteredInvoices.length,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} invoices`,
        }}
      />
    </Card>
  );
};

export default InvoiceList;
